import { Ionicons } from '@expo/vector-icons';
import React, { useState } from 'react';
import { View } from 'react-native';

import { ThemedText } from '@/components/themed-text';
import { PressableScale } from '@/components/ui/pressable-scale';
import { usePatchDraft } from '@/hooks/use-onboarding-interview';
import { PackService } from '@/types/activity-pack';
import { RECURRING_BOOKING_MODE } from '@/types/recurring-booking-mode';
import { formatDuration } from '@/utils/format-appointment';
import { formatCurrency } from '@/utils/format-currency';
import {
  EditableService,
  NEW_SERVICE,
  ServiceEditSheet,
} from './service-edit-sheet';
import { ServicePickerWidgetData, WidgetProps } from './widget-types';
import { WidgetCard } from './widget-card';

const PRIMARY = '#FD5001';

type Option = ServicePickerWidgetData['options'][number];

/**
 * **O cartão do serviço**: marcar o que ele faz e acertar valor, tempo e forma
 * de cobrança na mesma lista.
 *
 * Eram dois cartões seguidos — um para marcar, outro para precificar — e a
 * mesma lista aparecia duas vezes com regras diferentes. Junto, o passo é um
 * só: o valor e o tempo comuns do ramo já vêm em cinza, tocar no serviço abre
 * o ajuste, e "Pronto" grava tudo de uma vez.
 *
 * Serve também as sugestões do fim ("gente do seu ramo também oferece isso"),
 * que é a mesma interação com outro texto.
 *
 * Marcar é local: **só o botão grava**, para ele experimentar sem cada toque
 * virar uma ida ao servidor.
 */
export function ServicePickerWidget({
  data,
  editable,
  onAdvance,
}: WidgetProps<ServicePickerWidgetData>) {
  const patch = usePatchDraft();
  const isSuggestion = data.kind === 'suggestion';
  const [selected, setSelected] = useState<string[]>(() =>
    (data.options ?? []).filter(option => option.selected).map(o => o.name),
  );
  /** O que ele mudou no sheet, ainda não gravado. */
  const [edits, setEdits] = useState<Record<string, PackService>>({});
  const [custom, setCustom] = useState<PackService[]>([]);
  /** O sheet aberto: um serviço da lista, ou o "acrescentar outro" em branco. */
  const [editing, setEditing] = useState<EditableService | null>(null);
  const [done, setDone] = useState(false);

  const options: Option[] = [
    ...(data.options ?? []),
    ...custom.map(service => ({
      name: service.name,
      selected: true,
      answered: true,
      defaultPrice: service.defaultPrice ?? null,
      durationMinutes: service.durationMinutes ?? null,
      recurring: false,
      patch: service,
    })),
  ];

  /** A linha como está na tela: o que ele editou vence o que veio do ramo. */
  const shown = (option: Option): Option => {
    const edit = edits[option.name];
    if (!edit) return option;
    return {
      ...option,
      answered: true,
      defaultPrice: edit.defaultPrice ?? null,
      // Rótulo de edição ainda não gravada: o backend redesenha o cartão com
      // a formatação oficial assim que o "Pronto" grava.
      priceLabel:
        edit.defaultPrice != null
          ? formatCurrency(edit.defaultPrice)
          : 'a combinar',
      durationMinutes: edit.durationMinutes ?? null,
      durationLabel:
        edit.durationMinutes != null
          ? formatDuration(edit.durationMinutes)
          : 'duração combinada',
      recurring:
        edit.isSubscription ??
        (!!edit.recurringBookingMode &&
          edit.recurringBookingMode !== RECURRING_BOOKING_MODE.OFF),
      recurringLabel: null,
      patch: { ...option.patch, ...edit },
    };
  };

  const toggle = (name: string) =>
    setSelected(prev =>
      prev.includes(name)
        ? prev.filter(item => item !== name)
        : [...prev, name],
    );

  const alreadyIn = (data.options ?? [])
    .filter(option => option.selected)
    .map(option => option.name);

  const confirm = async () => {
    if (done) return;
    setDone(true);
    const services = options
      .filter(option => selected.includes(option.name))
      .map(option => shown(option).patch);
    // Desmarcar o que já estava no rascunho é remoção de verdade; o merge do
    // payload só acrescenta e corrigiria em vez de tirar.
    const removeServices = alreadyIn.filter(name => !selected.includes(name));

    if (services.length > 0 || removeServices.length > 0) {
      await patch.mutateAsync({
        payload: services.length > 0 ? { services } : undefined,
        removeServices: removeServices.length > 0 ? removeServices : undefined,
      });
    }
    // Fala provisória: o servidor devolve a versão oficial e ela substitui.
    onAdvance?.(
      isSuggestion ? 'Quero acrescentar esses.' : 'Marquei o que eu faço.',
    );
  };

  const skip = () => {
    setDone(true);
    onAdvance?.('Agora não.');
  };

  const canEdit = editable && !done;

  return (
    <>
      <WidgetCard
        title={data.title}
        icon={isSuggestion ? 'bulb-outline' : 'pricetags-outline'}
      >
        {data.hint ? (
          <ThemedText className="text-xs text-foreground/45">
            {data.hint}
          </ThemedText>
        ) : null}

        <View className="gap-2">
          {options.map(raw => {
            const option = shown(raw);
            const on = selected.includes(option.name);
            const faded = option.answered === false;
            return (
              <View
                key={option.name}
                className={`flex-row items-center gap-3 rounded-xl border px-3 py-2.5 ${
                  on
                    ? 'border-primary bg-primary/10'
                    : 'border-border/60 bg-neutral-50'
                }`}
              >
                <PressableScale
                  disabled={!canEdit}
                  onPress={() => toggle(option.name)}
                  className="flex-1 flex-row items-center gap-3"
                >
                  <Ionicons
                    name={on ? 'checkbox' : 'square-outline'}
                    size={18}
                    color={on ? PRIMARY : '#9ca3af'}
                  />
                  <View className="flex-1 gap-0.5">
                    <ThemedText className="text-sm font-medium text-foreground">
                      {option.name}
                    </ThemedText>
                    <View className="flex-row items-center gap-1.5">
                      <ThemedText
                        className={`shrink text-xs ${
                          faded ? 'text-foreground/35' : 'text-foreground/55'
                        }`}
                      >
                        {[option.durationLabel, option.priceLabel]
                          .filter(Boolean)
                          .join(' · ')}
                      </ThemedText>
                      {option.recurring ? (
                        <View className="shrink-0 rounded-full bg-primary/10 px-2 py-0.5">
                          <ThemedText className="text-[10px] font-semibold text-primary">
                            Plano mensal
                          </ThemedText>
                        </View>
                      ) : null}
                    </View>
                  </View>
                </PressableScale>

                {/* Ajustar só faz sentido no que ele marcou: editar o preço de
                    um serviço que ele não faz é trabalho jogado fora. */}
                {canEdit && on ? (
                  <PressableScale
                    onPress={() =>
                      setEditing({
                        name: option.name,
                        defaultPrice: option.defaultPrice ?? null,
                        durationMinutes: option.durationMinutes ?? null,
                        recurring: option.recurring,
                      })
                    }
                    className="p-1"
                  >
                    <Ionicons className="text-neutral-400" name="create-outline" size={16} />
                  </PressableScale>
                ) : null}
              </View>
            );
          })}

          {canEdit && data.allowCustom !== false ? (
            <PressableScale
              onPress={() => setEditing(NEW_SERVICE)}
              className="flex-row items-center justify-center gap-2 rounded-xl border border-dashed border-border py-2.5"
            >
              <Ionicons name="add" size={16} color={PRIMARY} />
              <ThemedText className="text-sm font-semibold text-primary">
                Acrescentar outro
              </ThemedText>
            </PressableScale>
          ) : null}
        </View>

        {canEdit ? (
          <View className="gap-2">
            <PressableScale
              onPress={() => void confirm()}
              disabled={patch.isPending}
              className="items-center rounded-full bg-primary py-2.5"
            >
              <ThemedText className="text-sm font-semibold text-white">
                {patch.isPending ? 'Salvando...' : data.confirmLabel}
              </ThemedText>
            </PressableScale>
            {data.skipLabel ? (
              <PressableScale onPress={skip} className="items-center py-1">
                <ThemedText className="text-xs font-semibold text-foreground/45">
                  {data.skipLabel}
                </ThemedText>
              </PressableScale>
            ) : null}
          </View>
        ) : null}
      </WidgetCard>

      {/* Um sheet só, para acrescentar e para ajustar: pedir o nome numa
          janela e o valor em outra fazia o prestador responder duas vezes a
          mesma coisa. O ajuste é local até o "Pronto" — gravar a cada sheet
          faria a lista responder o passo antes de ele terminar de conferir. */}
      <ServiceEditSheet
        service={editing}
        interval={data.defaultInterval}
        onSave={service => {
          setEdits(prev => ({ ...prev, [service.name]: service }));
          setSelected(prev =>
            prev.includes(service.name) ? prev : [...prev, service.name],
          );
          // Serviço que não está na lista do ramo entra como linha nova.
          setCustom(prev =>
            options.some(option => option.name === service.name) ||
            prev.some(item => item.name === service.name)
              ? prev
              : [...prev, service],
          );
          setEditing(null);
        }}
        onClose={() => setEditing(null)}
      />
    </>
  );
}

